import { useEffect, useRef, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import type { ContentItem } from '../../api/content';
import { TYPE_LABELS, LANG_LABELS } from './content-labels';
import { approvalNote, getDisplayStatusLabel, getDisplayStatusVariant, isImportedPost, staleApprovalDays } from './content-status';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { displayTitle, draftExcerpt, failedGeneration } from './content-duplicates';

const PERSONA_LABELS: Record<string, string> = {
  patrick: 'Patrick (CEO)',
  evrard: 'Evrard (CTO)',
  sequesto: 'SEQUESTO',
};

interface ContentItemCardProps {
  item: ContentItem;
  onSelect?: () => void;
  onDelete?: () => void;
  onReopen?: () => void;
  /** No longer offered on a card: archived items aren't re-generated from the library. Kept for callers. */
  onRegenerate?: () => void;
  isDeleting?: boolean;
  isReopening?: boolean;
  /** See onRegenerate. */
  isRegenerating?: boolean;
  /** Drop the type pill when every card in the grid has the same type (e.g. all LinkedIn posts). */
  hideType?: boolean;
  /** Drop the status pill when every card in the grid has the same status (e.g. all drafts). */
  hideStatus?: boolean;
  /** Older versions with the same title, folded under this card (see content-duplicates.ts). */
  olderVersions?: number;
  /** Show those older versions; the count line inside the card becomes a link. */
  onShowVersions?: () => void;
}

export function ContentItemCard({ item, onSelect, onDelete, onReopen, isDeleting = false, isReopening = false, hideType = false, hideStatus = false, olderVersions = 0, onShowVersions }: ContentItemCardProps) {
  const meta = item.metadata as Record<string, unknown>;
  const isGenerating = !!meta?.generating;
  const score = meta?.score as Record<string, number> | undefined;
  const persona = meta?.person as string | undefined;
  const websiteRefresh = meta?.websiteRefresh as Record<string, unknown> | undefined;
  const sentToSanityDraft = !!websiteRefresh?.lastPushedAt;
  const refreshMode = websiteRefresh?.refreshMode as 'scratch' | 'rewrite' | 'manual' | undefined;
  const isArchived = !!item.archivedAt;
  const failed = failedGeneration(item);
  const staleDays = staleApprovalDays(item);
  const canReopen = !!websiteRefresh && item.status === 'published' && !!onReopen && !isArchived;
  const approval = approvalNote(item);
  // An archived or failed card says one thing: how a page was refreshed and whether its
  // Sanity draft went out no longer matter, so those pills stay on live cards only.
  const live = !isArchived && !failed;

  return (
    // A full-height column so cards in one grid row line up, actions pinned to the bottom.
    <Card padding="sm" className="flex h-full flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {!hideType && <Badge variant="default">{TYPE_LABELS[item.type] ?? item.type}</Badge>}
          {/* One state per card: an archived item's last status ("Approved") no longer applies, and a
              stale approval's own pill ("Approved 89 days ago, not published") already says "Approved". */}
          {isArchived ? (
            <Badge variant="default">Archived</Badge>
          ) : staleDays !== null ? (
            <Badge variant="warning">Approved {staleDays} days ago, not published</Badge>
          ) : (
            !hideStatus && <Badge variant={getDisplayStatusVariant(item)}>{getDisplayStatusLabel(item)}</Badge>
          )}
          {isImportedPost(item) && (
            <Badge variant="default">Imported</Badge>
          )}
          {/* Facts, not states: neutral pills (indigo is for links, focus and selection). */}
          {live && refreshMode === 'scratch' && <Badge variant="default">From scratch</Badge>}
          {live && refreshMode === 'rewrite' && <Badge variant="default">From live page</Badge>}
          {live && refreshMode === 'manual' && <Badge variant="default">Manual import</Badge>}
          {live && sentToSanityDraft && <Badge variant="default">Sanity draft sent</Badge>}
          {item.language !== 'en' && (
            <Badge variant="default">{LANG_LABELS[item.language] ?? item.language}</Badge>
          )}
          {approval && <Badge variant={approval.variant}>{approval.label}</Badge>}
        </div>
        {score && (score.seo_score != null || score.geo_score != null) && (
          // Plain figures, not boxes inside the card.
          <div className="flex shrink-0 items-baseline gap-3 text-body-xs text-text-tertiary tabular-nums">
            {score.seo_score != null && <ScoreFigure label="SEO" value={score.seo_score} />}
            {score.geo_score != null && <ScoreFigure label="GEO" value={score.geo_score} />}
          </div>
        )}
      </div>

      {(persona || item.icpSegment) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-body-xs text-text-tertiary">
          {persona && <span>{PERSONA_LABELS[persona] ?? persona}</span>}
          {item.icpSegment && <span>{item.icpSegment.split(',').map((s) => s.replace(/_/g, ' ')).join(', ')}</span>}
        </div>
      )}

      <h3
        className="text-body-sm font-medium text-text-primary line-clamp-2 break-words cursor-pointer hover:text-text-secondary"
        onClick={onSelect}
        title={displayTitle(item.title)}
      >
        {failed
          ? olderVersions > 0
            ? `${olderVersions + 1} untitled drafts: generation failed`
            : 'Untitled draft: generation failed'
          : displayTitle(item.title)}
      </h3>
      {failed && (
        <p className="text-body-xs text-text-tertiary line-clamp-2 break-words" title={failed}>
          <Badge variant="error">Failed</Badge> <span className="ml-1">{failed}</span>
        </p>
      )}

      {/* A failed generation is over: no "Generating..." under its Failed line. */}
      {isGenerating && !failed ? (
        <p className="text-body-xs text-text-tertiary italic">Generating...</p>
      ) : item.body ? (
        <p className="text-body-xs text-text-secondary line-clamp-3">
          {draftExcerpt(item.body, 200, item.title)}
        </p>
      ) : null}

      {olderVersions > 0 && (
        <p className="text-body-xs text-text-tertiary">
          {onShowVersions ? (
            <button type="button" className="text-accent hover:underline" onClick={onShowVersions}>
              {failed ? `Show all ${olderVersions + 1} failed drafts` : `${olderVersions + 1} versions with this title`}
            </button>
          ) : failed ? (
            `${olderVersions + 1} failed drafts with this error`
          ) : (
            `${olderVersions + 1} versions with this title`
          )}
          ; the newest is shown
        </p>
      )}

      <div className="mt-auto flex items-center justify-between pt-1">
        <span className="text-body-xs text-text-tertiary">
          {formatDayMonthYear(item.updatedAt)}
        </span>
        <div className="flex gap-2">
          {onSelect && (
            <Button size="sm" variant="ghost" onClick={onSelect}>
              Open
            </Button>
          )}
          {canReopen && (
            <Button
              size="sm"
              variant="secondary"
              isLoading={isReopening}
              onClick={onReopen}
            >
              Re-open
            </Button>
          )}
          {onDelete && <CardMenu onDelete={onDelete} isDeleting={isDeleting} />}
        </div>
      </div>
    </Card>
  );
}

function ScoreFigure({ label, value }: { label: string; value: number }) {
  return (
    <span>
      {label} <span className="text-body-sm font-medium text-text-primary">{value.toFixed(1)}</span>
    </span>
  );
}

/** The rare, destructive actions, out of the card's face: one "⋯" menu instead of a red button on every card. */
function CardMenu({ onDelete, isDeleting }: { onDelete: () => void; isDeleting: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <Button
        size="sm"
        variant="ghost"
        isLoading={isDeleting}
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full right-0 z-10 mb-1 min-w-[8rem] rounded-sm border border-border-light bg-surface-primary py-1 shadow-sm"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-1.5 text-left text-body-sm text-error-dark hover:bg-surface-secondary"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            Delete…
          </button>
        </div>
      )}
    </div>
  );
}
